/*
 Welder Qualification Chart :
 1. Joint Type is a Mandatory field which will filter data according to  Groove Overlay and Tubesheet
 2. Process is fetched according to the WPQ no selected
 3. "getRequiredWPQNo" is used to get all the records of WPQ
 4. "WQCexportreportAll" is used to get data for excel
*/
import React, { useState, forwardRef, useEffect, useMemo, useCallback } from "react";
import { Row, Col, Container, Form, Button } from "react-bootstrap";
import { showWarning, showSuccess, showInfo } from "../utility/ui/notifications/Toaster";
import { GridLoader } from "react-spinners";
import { SearchSelect } from "../utility/forms/inputs/search-select";
import { ExportToExcel } from "./WQChartExcel";
import { DateInput } from "../utility/common/helpers/DateComponent";
import { todayDate } from "../utility/common/helpers/TodayDate";
import { WQCexportreportAll, getRequiredWPQNo } from "../../Services/wpq_groove-services";
import { isRealNumber } from "../../functions/validations";
import { getAllWelder } from "../../Services/Welder-services";
import { getdetailsById } from "../../Services/wpq_details-service";
import { normalstyle } from "../../functions/welding-functions";
import { PlantLocation, Jointstatus } from "../utility/common/TransactionComponents/TransactionEntity";

const WQChartReport = forwardRef((props, ref) => {
  const [wpqData, setWpqData] = useState([]);
  const [welderDetails, setWelderDetails] = useState([]);
  const [processCategories, setProcessCategories] = useState([]);
  const [datevalue, setdatevalue] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const reporName = "WQChartReport";
  const [wqObj, setwqObj] = useState({
    WQC_Joint_Type: "",
    WQC_WelderId: "",
    WQC_WPQRecordNo: "",
    WQC_WPQProcess: "",
    WQC_From_Date: "",
    WQC_To_Date: "",
    WQC_Inspection: "",
    WQC_Pipe_Diam: "",
    WQC_PlantLocation: "",
    WQC_Process_Status: "",
  });

  const [error, setError] = useState({
    WQC_Joint_Type: {},
    WQC_WelderId: {},
    WQC_WPQRecordNo: {},
    WQC_WPQProcess: {},
    WQC_From_Date: {},
    WQC_To_Date: {},
    WQC_Inspection: {},
    WQC_Pipe_Diam: {},
    WQC_PlantLocation: {},
    WQC_Process_Status: {},
  });

  const ProcessStatus = useMemo(() => [
    { value: "Active", name: "Active" },
    { value: "Inactive", name: "Inactive" },
  ], []);

  const fetchWelder = useCallback(async () => {
    const result = await getAllWelder();
    if (result) {
      const modifiedResult = result.map((welder) => ({
        ...welder,
        wld_salarycode: welder.wld_salarycode || welder.wld_code,
      }));
      setWelderDetails(modifiedResult);
    }
  }, []);

  const fetchWPQDetails = useCallback(async () => {
    const jointType = wqObj?.WQC_Joint_Type;
    if (jointType) {
      const result = await getRequiredWPQNo(jointType);
      setWpqData(result);
    } else {
      setWpqData([]);
    }
  }, [wqObj.WQC_Joint_Type]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setwqObj((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  useEffect(() => {
    fetchWelder();
  }, [fetchWelder]);

  useEffect(() => {
    fetchWPQDetails();
  }, [fetchWPQDetails]);

  const selectedWPQNo = useMemo(() =>
    wpqData?.filter(
      (item) =>
        item.WPQ_RecordNo &&
        item.WPQ_RecordNo !== "undefined"
    ) || [], [wpqData]
  );

  const Inspection = useMemo(() => {
    const selectedInspection = wpqData?.filter(
      (item) =>
        item.WPQ_Inspection &&
        item.WPQ_Inspection !== "undefined"
    ) || [];

    return selectedInspection.filter(
      (value, index, self) =>
        index ===
        self.findIndex((obj) => obj.WPQ_Inspection === value.WPQ_Inspection)
    );
  }, [wpqData]);

  const getEachProcess = useCallback(async (wpqNo) => {
    const detailProcess = await getdetailsById(wqObj.WQC_WelderId, wpqNo);
    let selectedJointProcess = "";
    if (wqObj.WQC_Joint_Type === "Tubesheet") {
      for (const item of detailProcess) {
        selectedJointProcess += item.WPQ_Process + ","
      }
    }
    else {
      for (const item of detailProcess) {
        selectedJointProcess += item.WPQ_Process_Category?.split("#")[0] + ","
      }
    }
    if (!detailProcess?.length > 0) {
      setProcessCategories([]);
      return;
    }
    const uniqueProcesses = [...new Set(selectedJointProcess.split(",").filter(Boolean))];
    setProcessCategories(
      uniqueProcesses.map((process) => ({ label: process, value: process }))
    );
  }, [wpqData, wqObj.WQC_WelderId]);

  const validate = useCallback(() => {
    if (wqObj.WQC_From_Date && wqObj.WQC_To_Date) {
      if (wqObj.WQC_From_Date > wqObj.WQC_To_Date) {
        setError(prev => ({
          ...prev,
          WQC_From_Date: { status: true },
          WQC_To_Date: { status: true },
        }));
        showWarning("To Date Should be Greater than or Equal to From Date");
        return true;
      }
    }
    return false;
  }, [wqObj.WQC_From_Date, wqObj.WQC_To_Date]);

  const initialState = useMemo(() => ({
    WQC_Joint_Type: "",
    WQC_WelderId: "",
    WQC_WPQRecordNo: "",
    WQC_WPQProcess: "",
    WQC_From_Date: "",
    WQC_To_Date: "",
    WQC_Inspection: "",
    WQC_Pipe_Diam: "",
    WQC_PlantLocation: "",
    WQC_Process_Status: "",
  }), []);

  const handleReset = useCallback(() => {
    setwqObj(initialState);
    setError({
      WQC_Joint_Type: {},
      WQC_WelderId: {},
      WQC_WPQRecordNo: {},
      WQC_WPQProcess: {},
      WQC_From_Date: {},
      WQC_To_Date: {},
      WQC_Inspection: {},
      WQC_Pipe_Diam: {},
      WQC_PlantLocation: {},
      WQC_Process_Status: {},
    });
    setdatevalue(false);
    setResetKey(prev => prev + 1);
  }, [initialState]);

  const processData = useCallback((headerresult, jointType) => {
    console.log('processData called with:', headerresult.length, 'records, jointType:', jointType);
    const Data = [];

    // Optimized getValue with memoization    
    const getValue = (detail, fieldName) => {
      const field = detail?.[fieldName];
      let result = "";

      if (!field) {
        return result;
      }

      if (!Array.isArray(field)) {
        result = field.toString();
      } else if (fieldName === 'WPQ_FM_Weld_Deposit_Thickness') {
        const rangeQualified = field[1]?.Range_Qualified?.[0]?.Range_Qualified1?.[0]?.[0];
        result = rangeQualified === "" ? "No Limit" : field[1]?.Range_Qualified?.[0]?.Range_Qualified1?.[0] || "";
      } else {
        result = field[1]?.Range_Qualified?.[0] || "";
      }

      return result;
    };

    // Optimized getPositions with early returns
    const positionCache = new Map();
    const getPositions = (field) => {
      if (!field?.[1]?.Range_Qualified || !Array.isArray(field[1].Range_Qualified)) return "";

      const fieldKey = JSON.stringify(field[1].Range_Qualified);
      if (positionCache.has(fieldKey)) {
        return positionCache.get(fieldKey);
      }

      const result = field[1].Range_Qualified
        .filter(item => Array.isArray(item) && item[1] === "true")
        .map(item => item[0])
        .join(",");

      positionCache.set(fieldKey, result);
      return result;
    };

    for (const detail of headerresult) {
      // Early filtering
      if (wqObj.WQC_WPQProcess && detail.WPQ_Process !== wqObj.WQC_WPQProcess) continue;

      // Determine real-time active/inactive status based on WPQ_Welder_ExpiresOn
      const expiresOn = detail.WPQ_Welder_ExpiresOn;
      let realStatus = "Inactive";
      if (expiresOn) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expiryDate = new Date(expiresOn);
        realStatus = expiryDate >= today ? "Active" : "Inactive";
      }

      if (wqObj.WQC_Process_Status !== "" && realStatus !== wqObj.WQC_Process_Status) continue;

      // Date processing
      const dt = detail.WPQ_Date;
      if (!dt) continue;

      const slipDate = new Date(dt.split('-').reverse().join('-'));

      if (compareDates(wqObj, slipDate)) {
        const processCat = detail?.WPQ_Process_Category?.split("#");

        // Optimize object creation with pre-computed values
        const baseData = {
          A: "",
          B: detail.WPQ_WelderId,
          C: detail.WPQ_RecordNo,
          D: detail.WPQ_WelderName,
          E: jointType === "Tubesheet" ? detail.WPQ_Process : processCat?.[0] || "",
          F: detail.WPQ_Type || "",
          G: detail.WPQ_Welder_ExpiresOn,
        };
        if (jointType === "Tubesheet") {
          // Pre-compute expensive operations
          const widthLigament = detail.WPQ_Width_Ligament?.[1]?.Range_Qualified?.[0] || "";
          const tubePitch = detail.WPQ_Tube_Pitch?.[1]?.Range_Qualified?.[0] || "";
          const grooveDepth = detail.WPQ_Groove_Depth?.[1]?.Range_Qualified?.[0] || "";
          const tubeProjection = detail.WPQ_Tube_Projection?.[1]?.Range_Qualified?.[0] || "";
          const pattern = detail.WPQ_Pattern?.[1]?.Range_Qualified?.[0] || "";
          const grooveAngle = detail.WPQ_Groove_Angle?.[1]?.Range_Qualified?.[0] || "";

          Data.push({
            ...baseData,
            H: widthLigament,
            I: "Tube to Tubesheet",
            J: detail.WPQ_BM_WTBM_Dia,
            K: detail.WPQ_BM_WTBM_Thk,
            L: detail.WPQ_BM_RangeQualified_Thk,
            M: detail.WPQ_BM_RangeQualified_Dia,
            N: tubePitch,
            O: grooveDepth,
            P: tubeProjection,
            Q: widthLigament,
            R: pattern,
            S: grooveAngle,
            T: detail.WPQ_BM_BM_Pno,
            U: detail.WPSD_FM_FNo,
            V: detail.WPSD_FM_Ano,
            W: detail.WPQ_FM_PreFillerMetal,
            X: detail.WPQ_FM_WithFillerMetal,
            Y: detail.WPQ_FM_FillerProduct,
            Z: detail.WPQ_FM_Inserts,
            AA: detail.WPQD_WP_NoofPasses,
            AB: detail.WPQD_WP_WeldPosition,
            AC: detail.WPQD_WP_WeldProgression,
            AD: detail.WPQD_WP_PolarityOrCurrent?.[0]?.Value_A?.[0] || detail.WPQD_WP_PolarityOrCurrent?.[1]?.Value_B?.[0] || "",
            AE: detail.WPQD_WP_Automatic_Arc_Voltage,
            AF: "",
            AG: "",
            AH: detail.WPQ_PlantLocation,
            AI: realStatus
          });
        } else if (jointType === "Overlay") {
          // Batch getValue calls for better performance
          const overlayFields = {
            H: getValue(detail, 'WPQ_BM_WTBM_Pno'),
            I: getValue(detail, 'WPQ_FM_F_No'),
            J: getPositions(detail.WPQ_Overlay_610),
            K: getPositions(detail.WPQ_Overlay_73to610),
            L: getPositions(detail.WPQ_Overlay_73),
            M: detail.WPQ_BM_WTBM_Thk,
            N: detail.WPQ_BMT_Pipe_Diameter_RangeQ_Overlay,
            O: getValue(detail, 'WPQ_Detail_Backing'),
            P: getValue(detail, 'WPQ_FM_Backing_Gas'),
            Q: getValue(detail, 'WPQ_FM_Progression'),
            R: detail.WPQ_Inspection,
            S: getValue(detail, 'WPQ_FM_Arc_transfermode'),
            T: getValue(detail, 'WPQ_FM_Direct_VisualControl'),
            U: getValue(detail, 'WPQ_FM_Automatic_jointTracking'),
            V: getValue(detail, 'WPQ_FM_filler_Wire'),
            W: getValue(detail, 'WPQ_FM_Consumable_Insert'),
            X: getValue(detail, 'WPQ_FM_Product_Form'),
            Y: getValue(detail, 'WPQ_FM_Current_Polarity'),
            Z: getValue(detail, 'WPQ_FM_Single_Pass_PerSide'),
            AA: getValue(detail, 'WPQ_PlantLocation'),
            AB: realStatus
          };

          Data.push({ ...baseData, ...overlayFields });
        } else {
          // Batch process groove/fillet fields
          const grooveFields = {
            H: detail.WPQ_BM_BM_Pno,
            I: getValue(detail, 'WPQ_FM_F_No'),
            J: getPositions(detail.WPQ_Groove_610),
            K: getPositions(detail.WPQ_Groove_73to610),
            L: getPositions(detail.WPQ_Groove_73),
            M: getPositions(detail.WPQ_Fillet_610),
            N: getPositions(detail.WPQ_Fillet_73to610),
            O: getPositions(detail.WPQ_Fillet_73),
            P: getValue(detail, 'WPQ_FM_Weld_Deposit_Thickness'),
            Q: detail.WPQ_BMT_Pipe_Diameter_RangeQ_Overlay,
            R: getValue(detail, 'WPQ_Detail_Backing'),
            S: getValue(detail, 'WPQ_FM_Backing_Gas'),
            T: getValue(detail, 'WPQ_FM_Progression'),
            U: detail.WPQ_Inspection,
            V: getValue(detail, 'WPQ_FM_Arc_transfermode'),
            W: getValue(detail, 'WPQ_FM_Direct_VisualControl'),
            X: getValue(detail, 'WPQ_FM_Automatic_jointTracking'),
            Y: getValue(detail, 'WPQ_FM_filler_Wire'),
            Z: getValue(detail, 'WPQ_FM_Consumable_Insert'),
            AA: getValue(detail, 'WPQ_FM_Product_Form'),
            AB: getValue(detail, 'WPQ_FM_Current_Polarity'),
            AC: getValue(detail, 'WPQ_FM_Single_Pass_PerSide'),
            AD: detail.WPQ_PlantLocation,
            AE: realStatus
          };

          Data.push({ ...baseData, ...grooveFields });
        }
      }
    }
    return Data;
  }, [wqObj]);

  const getExcelConfig = useCallback((jointType) => {
    const configs = {
      Tubesheet: {
        titleCell: "E3",
        titleWelderRange: "G1:Y5",
        tbodyHeadRange: "A6:AI6",
        tbodyRange: "A7",
        blankRow: "A5:AC5",
        width: [
          { col: "A", width: 2.5 }, { col: "B", width: 13.7 }, { col: "C", width: 13.7 },
          { col: "D", width: 30 }, { col: "E", width: 18 }, { col: "F", width: 12 },
          { col: "G", width: 16 }, { col: "H", width: 30 }, { col: "I", width: 30 },
          { col: "J", width: 30 }, { col: "K", width: 30 }, { col: "L", width: 30 },
          { col: "M", width: 30 }, { col: "N", width: 30 }, { col: "O", width: 30 },
          { col: "P", width: 30 }, { col: "Q", width: 30 }, { col: "R", width: 20 },
          { col: "S", width: 20 }, { col: "T", width: 20 }, { col: "U", width: 20 },
          { col: "V", width: 20 }, { col: "W", width: 30 }, { col: "X", width: 30 },
          { col: "Y", width: 30 }, { col: "Z", width: 30 }, { col: "AA", width: 30 },
          { col: "AB", width: 30 }, { col: "AC", width: 38 }, { col: "AD", width: 38 },
          { col: "AE", width: 38 }, { col: "AF", width: 38 }, { col: "AG", width: 38 },
          { col: "AH", width: 20 }, { col: "AI", width: 20 }
        ]
      },
      Overlay: {
        titleCell: "E3",
        titleWelderRange: "G1:Y5",
        tbodyHeadRange: "A6:AB6",
        tbodyRange: "A7",
        blankRow: "A5:AC5",
        width: [
          { col: "A", width: 2.5 }, { col: "B", width: 13.7 }, { col: "C", width: 13.7 },
          { col: "D", width: 20.7 }, { col: "E", width: 18 }, { col: "F", width: 12 },
          { col: "G", width: 16 }, { col: "H", width: 30 }, { col: "I", width: 10 },
          { col: "J", width: 30 }, { col: "K", width: 30 }, { col: "L", width: 30 },
          { col: "M", width: 30 }, { col: "N", width: 30 }, { col: "O", width: 30 },
          { col: "P", width: 30 }, { col: "Q", width: 30 }, { col: "R", width: 20 },
          { col: "S", width: 20 }, { col: "T", width: 25 }, { col: "U", width: 25 },
          { col: "V", width: 25 }, { col: "W", width: 30 }, { col: "X", width: 30 },
          { col: "Y", width: 30 }, { col: "Z", width: 38 }, { col: "AA", width: 30 },
          { col: "AB", width: 30 }, { col: "AC", width: 38 }
        ]
      },
      default: {
        titleCell: "E3",
        titleWelderRange: "G1:Y5",
        tbodyHeadRange: "A6:AE6",
        tbodyRange: "A7",
        blankRow: "A5:AC5",
        width: [
          { col: "A", width: 2.5 }, { col: "B", width: 13.7 }, { col: "C", width: 13.7 },
          { col: "D", width: 20.7 }, { col: "E", width: 18 }, { col: "F", width: 12 },
          { col: "G", width: 16 }, { col: "H", width: 30 }, { col: "I", width: 10 },
          { col: "J", width: 30 }, { col: "K", width: 30 }, { col: "L", width: 30 },
          { col: "M", width: 30 }, { col: "N", width: 30 }, { col: "O", width: 30 },
          { col: "P", width: 30 }, { col: "Q", width: 30 }, { col: "R", width: 20 },
          { col: "S", width: 20 }, { col: "T", width: 20 }, { col: "U", width: 20 },
          { col: "V", width: 20 }, { col: "W", width: 30 }, { col: "X", width: 30 },
          { col: "Y", width: 30 }, { col: "Z", width: 30 }, { col: "AA", width: 30 },
          { col: "AB", width: 30 }, { col: "AC", width: 38 }, { col: "AD", width: 20 },
          { col: "AE", width: 20 }
        ]
      }
    };
    return configs[jointType] || configs.default;
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!validate()) {
      if (!wqObj.WQC_Joint_Type) {
        showWarning("Joint Type is Mandatory");
        return;
      }
      if (error.WQC_Pipe_Diam.status) {
        return;
      }

      setLoading(true);
      try {
        showInfo("Fetching data... This may take a moment for large datasets.");

        const result = await WQCexportreportAll(wqObj);
        console.log('WQCexportreportAll returned:', result?.length, 'records');

        if (!result?.length) {
          showInfo("There are no records matching to filters.");
          return;
        }

        const Data = processData(result, wqObj.WQC_Joint_Type);

        const filename = `WQC-${wqObj.WQC_Joint_Type}`;
        const sheetName = "Report_WQChart";
        const excelHeaders = {
          Overlay: "Welder's / Welding Operator's Qualifications Chart-Overlay",
          Tubesheet: "Welder's / Welding Operator's Qualifications Chart- Tube to Tubesheet",
          default: "Welder's / Welding Operator's Qualifications Chart-Groove/Fillet"
        };
        const excelHeader = excelHeaders[wqObj.WQC_Joint_Type] || excelHeaders.default;
        const currentDate = new Date();
        const formattedDate = currentDate.toLocaleDateString("en-GB");
        const PrintDate = `As on: ${formattedDate}`;
        const config = getExcelConfig(wqObj.WQC_Joint_Type);
        const Headings = [
          { col: "G2", value: excelHeader },
          { col: "K2", value: PrintDate },
          { col: "C4", value: "" },
        ];

        ExportToExcel(
          filename,
          config.width,
          result,
          Data,
          {
            titleCell: config.titleCell,
            titleWelderRange: config.titleWelderRange,
            tbodyHeadRange: config.tbodyHeadRange,
            tbodyRange: config.tbodyRange,
            blankRow: config.blankRow,
          },
          Headings,
          {
            sheetName,
            reporName,
            type: wqObj.WQC_Joint_Type,
          }
        );
        showSuccess(`Excel report generated successfully with ${Data.length} records!`);
      } catch (error) {
        console.error('Export failed:', error);
        showWarning('An error occurred while processing the request. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  }, [validate, wqObj, error.WQC_Pipe_Diam.status, processData, getExcelConfig, reporName]);

  const compareDates = useCallback((wqObj, lastWeldDate) => {
    if (wqObj.WQC_From_Date && new Date(wqObj.WQC_From_Date) > lastWeldDate) {
      return false;
    }
    if (wqObj.WQC_To_Date && lastWeldDate > new Date(wqObj.WQC_To_Date)) {
      return false;
    }
    return true;
  }, []);

  return (
    <>
      {loading && (
        <div className="loader-overlay">
          <GridLoader size={10} className="pageloader" />
        </div>
      )}
      <Container fluid>
        <Row className="RowHeight mb-4 ml-2">
          <Col md={12} sm={12} xs={12} className="ListTitle">
            Welder Qualification Chart Filters
          </Col>
        </Row>
        <div className="px-2">
          <Row className="gx-3">
            <Col md={3}>
              <Form.Group className="ListW-input-select">
                <div className="search-select">
                  <SearchSelect
                    id="WQC_Joint_Type"
                    name="WQC_Joint_Type"
                    className={""}
                    stylegreen={normalstyle}
                    data={Jointstatus}
                    valueField={"value"}
                    value={wqObj?.WQC_Joint_Type}
                    labelField={"name"}
                    placeholder=""
                    onChange={(ProjectList) => {
                      setwqObj(prev => ({
                        ...initialState,
                        WQC_Joint_Type: ProjectList.value,
                      }));
                    }}
                  ></SearchSelect>
                </div>
                <Form.Label>Joint Type</Form.Label>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="ListW-input-select">
                <div className="search-select">
                  <SearchSelect
                    id="WQC_WelderId"
                    name="WQC_WelderId"
                    className={""}
                    stylegreen={normalstyle}
                    data={welderDetails}
                    valueField={"wld_salarycode"}
                    value={wqObj?.WQC_WelderId}
                    labelField={"wld_salarycode"}
                    placeholder=""
                    onChange={(ProjectList) => {
                      setwqObj((prev) => ({
                        ...prev,
                        WQC_WelderId: ProjectList.value,
                      }));
                    }}
                  ></SearchSelect>
                </div>
                <Form.Label>Welder Code</Form.Label>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="ListW-input-select">
                <div className="search-select">
                  <SearchSelect
                    id="WQC_WPQRecordNo"
                    name="WQC_WPQRecordNo"
                    className={""}
                    stylegreen={normalstyle}
                    data={selectedWPQNo}
                    valueField={"WPQ_RecordNo"}
                    value={wqObj?.WQC_WPQRecordNo}
                    labelField={"WPQ_RecordNo"}
                    placeholder=""
                    onChange={(wpqNo) => {
                      setwqObj(prev => ({
                        ...prev,
                        WQC_WPQRecordNo: wpqNo.value,
                        WQC_WPQProcess: "",
                      }));
                      getEachProcess(wpqNo.value);
                    }}
                  ></SearchSelect>
                </div>
                <Form.Label>WPQ No</Form.Label>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="ListW-input-select">
                <div className="search-select">
                  <SearchSelect
                    id="WQC_WPQProcess"
                    name="WQC_WPQProcess"
                    className={""}
                    stylegreen={normalstyle}
                    data={processCategories}
                    valueField={"value"}
                    value={wqObj?.WQC_WPQProcess}
                    labelField={"label"}
                    placeholder=""
                    onChange={(ProjectList) => {
                      setwqObj(prev => ({
                        ...prev,
                        WQC_WPQProcess: ProjectList.value,
                      }));
                    }}
                  ></SearchSelect>
                </div>
                <Form.Label>Process Category</Form.Label>
              </Form.Group>
            </Col>
            <Col md={3}>
              <DateInput
                key={`from-date-${resetKey}`}
                divClass="Date-input w-100"
                label="Last Weld Date (From)"
                className="groove_datecolor"
                name="WQC_From_Date"
                todayDate={todayDate}
                obj={wqObj}
                setobj={setwqObj}
                datevalue={datevalue}
                setdatevalue={setdatevalue}
                todate={wqObj.WQC_To_Date}
                setError={setError}
                status={error.WQC_From_Date.status}
                message={error.WQC_From_Date.message}
              />
            </Col>
            <Col md={3}>
              <DateInput
                key={`to-date-${resetKey}`}
                divClass="Date-input w-100"
                label="Last Weld Date (To)"
                className="groove_datecolor"
                name="WQC_To_Date"
                todayDate={todayDate}
                obj={wqObj}
                fromdate={wqObj.WQC_From_Date}
                setobj={setwqObj}
                setdatevalue={setdatevalue}
                datevalue={datevalue}
                setError={setError}
                status={error.WQC_To_Date.status}
                message={error.WQC_To_Date.message}
              />
            </Col>
            <Col md={3}>
              <Form.Group className="ListW-input-select">
                <div className="search-select">
                  <SearchSelect
                    id="WQC_Inspection"
                    name="WQC_Inspection"
                    className={""}
                    stylegreen={normalstyle}
                    data={Inspection}
                    valueField={"WPQ_Inspection"}
                    value={wqObj?.WQC_Inspection}
                    labelField={"WPQ_Inspection"}
                    placeholder=""
                    onChange={(ProjectList) => {
                      setwqObj(prev => ({
                        ...prev,
                        WQC_Inspection: ProjectList.value,
                      }));
                    }}
                  ></SearchSelect>
                </div>
                <Form.Label>Inspection Agency</Form.Label>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <div className="T-input w-100">
                  <input
                    type="text"
                    id="WQC_Pipe_Diam"
                    name="WQC_Pipe_Diam"
                    placeholder=" "
                    className={error.WQC_Pipe_Diam.status ? "err-input" : ""}
                    value={wqObj.WQC_Pipe_Diam}
                    onChange={handleChange}
                    onInput={(e) => {
                      let error = isRealNumber(e.target.value);
                      setError((prevState) => ({
                        ...prevState,
                        WQC_Pipe_Diam: error,
                      }));
                    }}
                  />
                  <Form.Label htmlFor="WQC_Pipe_Diam">
                    Pipe Diameter(Less Than Equal To)
                  </Form.Label>
                </div>
                <div className="required-text">
                  {error.WQC_Pipe_Diam.status && (
                    <Form.Text className="text-danger">
                      {error.WQC_Pipe_Diam.message}
                    </Form.Text>
                  )}
                </div>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="ListW-input-select">
                <div className="search-select">
                  <SearchSelect
                    id="WQC_PlantLocation"
                    name="WQC_PlantLocation"
                    className={""}
                    stylegreen={normalstyle}
                    data={PlantLocation}
                    valueField={"value"}
                    value={wqObj?.WQC_PlantLocation}
                    labelField={"name"}
                    placeholder=""
                    onChange={(ProjectList) => {
                      setwqObj(prev => ({
                        ...prev,
                        WQC_PlantLocation: ProjectList.value,
                      }));
                    }}
                  ></SearchSelect>
                </div>
                <Form.Label>Plant Location</Form.Label>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="ListW-input-select">
                <div className="search-select">
                  <SearchSelect
                    id="WQC_Process_Status"
                    name="WQC_Process_Status"
                    className={""}
                    stylegreen={normalstyle}
                    data={ProcessStatus}
                    valueField={"value"}
                    value={wqObj?.WQC_Process_Status}
                    labelField={"name"}
                    placeholder=""
                    onChange={(ProjectList) => {
                      setwqObj((prev) => ({
                        ...prev,
                        WQC_Process_Status: ProjectList.value,
                      }));
                    }}
                  ></SearchSelect>
                </div>
                <Form.Label>Status</Form.Label>
              </Form.Group>
            </Col>
          </Row>
        </div>
        <Row>
          <Col>
            <Button onClick={handleReset} className="btn-reset-color semiBoldToggleButton leftAlignedButton">
              Reset
            </Button>
          </Col>
          <Col>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="btn-add-color semiBoldToggleButton rightAlignedButton"
            >
              {loading ? 'Processing...' : 'Submit'}
            </Button>
          </Col>
        </Row>
      </Container>
    </>
  );
});

export default WQChartReport;